<template>
  <main class="page-operative page-operative-data-analysis page-operative-data-analysis-costs page-operative-data-analysis-costs-list d-flex flex-column h-100">
    <div class="flex-grow-1 container-fluid d-flex flex-column h-100">
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle class="mb-2 mb-md-0">Costi del personale</PageTitle>

        <app-router-link v-if="userCanEdit" class="btn btn-primary" :to="{ name: 'operative-company.data-analysis.personnel.add' }">
          Aggiungi costo del personale
        </app-router-link>
      </div>

      <div class="flex-grow-1 d-flex flex-column h-100 overflow-auto">
        <LoadingDataLoader v-if="isLoadingData"></LoadingDataLoader>
        <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>
        <template v-else>
          <div class="d-flex align-items-center justify-content-between">
            <ForecastListSwitcher :viewMode="viewMode" @update:viewMode="setViewMode" />

            <BFormCheckbox v-if="viewMode === 'forecast'" v-model="isSticky" class="no-select" :switch="true">
              Blocca aggregato in alto
            </BFormCheckbox>
          </div>

          <template v-if="viewMode === 'forecast' && data">
            <div class="overflow-auto report-texts-size">
              <OperativeTable dense sticky>
                <template v-for="(table, i) in aggregateTableRows">
                  <OperativeTableAggregateTable v-if="i === 0" :key="'aggregate-' + i" :sticky="isSticky">
                    <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years>{{ table.name }}</OperativeTableBodyHeader>
                    <OperativeTableBodyRow :values="table.data.ral">
                      Ral
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.oneriSociali">
                      Oneri sociali
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.accantonamentoTfr">
                      Accantonamento TFR
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.altriCosti">
                      Altri costi
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.ritenute">
                      Ritenute
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableBodyRow :values="table.data.fondoTfr">
                      Fondo TFR
                      <template #cell-content="{ value }">{{ value | money }}</template>
                    </OperativeTableBodyRow>
                    <OperativeTableEmptyBodyRow v-if="i !== aggregateTableRows.length - 1" :fixedNumColumns="aggregateTableHeadings.length + 1" />
                  </OperativeTableAggregateTable>
                  <tbody v-else :key="'item-' + i">
                    <OperativeTableBodyHeader :years="aggregateTableHeadings" :show-years="openedRows[table.id]">
                      <p class="mb-0 px-1" @click="toggleRow(table.id)" style="user-select: none; cursor: pointer">
                        <i v-if="openedRows[table.id]" class="fas fa-caret-up"></i>
                        <i v-else class="fas fa-caret-down"></i>
                        {{ table.name }}
                      </p>
                    </OperativeTableBodyHeader>
                    <template v-if="openedRows[table.id]">
                      <OperativeTableBodyRow :values="table.data.monthHoursAutomatico">
                        Ore mensili teoriche
                        <template #cell-content="{ value }">{{ value }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.monthHoursBudget">
                        Ore mensili a Budget
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledMonthHoursBudgetEdit[col]">
                            {{ value }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'monthHoursBudget', 'Ore mensili a Budget', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.monthHoursConsuntivo">
                        Ore mensili a Consuntivo
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledMonthHoursConsuntivoEdit[col]">
                            {{ value }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'monthHoursConsuntivo', 'Ore mensili a Consuntivo', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.monthHours">
                        Ore mensili
                        <template #cell-content="{ value }">{{ value }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow v-if="isModuleProjectsEnabled" :values="table.data.monthProjectsHours">
                        Ore mensili assegnate a progetti
                        <template #cell-content="{ value }">{{ value }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow v-if="isModuleProjectsEnabled" :values="table.data.monthStructuralHours">
                        Ore mensili assegnate a struttura
                        <template #cell-content="{ value }">{{ value }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.ralAutomatico">
                        RAL Teorica
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.ralBudget">
                        RAL a Budget
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledRalBudgetEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'ralBudget', 'Ral a Budget', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.ralConsuntivo">
                        RAL a Consuntivo
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledRalConsuntivoEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'ralConsuntivo', 'Ral a Consuntivo', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.ral">
                        RAL
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>

                      <OperativeTableBodyRow :values="table.data.oneriSocialiAutomatico">
                        Oneri sociali teorici
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.oneriSocialiBudget">
                        Oneri sociali a Budget
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiBudgetEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'oneriSocialiBudget', 'Oneri sociali a Budget', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.oneriSocialiConsuntivo">
                        Oneri sociali a Consuntivo
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiConsuntivoEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'oneriSocialiConsuntivo', 'Oneri sociali a Consuntivo', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.oneriSociali">
                        Oneri sociali
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>

                      <OperativeTableBodyRow :values="table.data.accantonamentoTfrAutomatico">
                        Accantonamento TFR teorico
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.accantonamentoTfrBudget">
                        Accantonamento TFR a Budget
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiBudgetEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'accantonamentoTfrBudget', 'Accantonamento TFR a Budget', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.accantonamentoTfrConsuntivo">
                        Accantonamento TFR a Consuntivo
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiConsuntivoEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'accantonamentoTfrConsuntivo', 'Accantonamento TFR a Consuntivo', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.accantonamentoTfr">
                        Accantonamento TFR
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>

                      <OperativeTableBodyRow :values="table.data.altriCostiAutomatico">
                        Altri costi teorici
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.altriCostiBudget">
                        Altri costi a Budget
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiBudgetEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'altriCostiBudget', 'Altri costi a Budget', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.altriCostiConsuntivo">
                        Altri costi a Consuntivo
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiConsuntivoEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'altriCostiConsuntivo', 'Altri costi a Consuntivo', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.altriCosti">
                        Altri costi
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>

                      <OperativeTableBodyRow :values="table.data.ritenuteAutomatico">
                        Ritenute teoriche
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.ritenuteBudget">
                        Ritenute a Budget
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiBudgetEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'ritenuteBudget', 'Ritenute a Budget', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.ritenuteConsuntivo">
                        Ritenute a Consuntivo
                        <template #cell-content="{ value, col }">
                          <template v-if="!userCanEdit || !table.data.enabledAltriCostiConsuntivoEdit[col]">
                            {{ value | money }}
                          </template>
                          <template v-else>
                            <span>
                              {{ value | money }}
                            </span>
                            <button class="btn btn-link text-primary small pr-0 text-right d-inline py-0 pl-1" @click="showAddBudgetModal(table.id, table.name, 'ritenuteConsuntivo', 'Ritenute a Consuntivo', table.data.month[col], table.data.year[col], value)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                          </template>
                        </template>
                      </OperativeTableBodyRow>
                      <OperativeTableBodyRow :values="table.data.ritenute">
                        Ritenute
                        <template #cell-content="{ value }">{{ value | money }}</template>
                      </OperativeTableBodyRow>
                    </template>
                    <OperativeTableEmptyBodyRow v-if="i !== aggregateTableRows.length - 1" :fixedNumColumns="aggregateTableHeadings.length + 1" />
                  </tbody>
                </template>
              </OperativeTable>
            </div>

            <BModal :title="currentModalInfo.title" dialog-class="modal--type-dialog" id="edit-value-modal" ref="edit-value-modal" hide-footer centered scrollable>
              <form @submit.prevent="saveCurrentModalInfo">
                <div class="form-group row align-items-end py-4">
                  <div class="col">
                    <span class="font-weight-bold d-block">
                      {{ currentModalInfo.typeName }}
                    </span>
                    <span class="text-muted small">
                      Mensilità: {{ currentModalInfo.month }}/{{ currentModalInfo.year }}
                    </span>
                  </div>
                  <div class="col">
                    <input type="number" v-model="currentModalInfo.value" class="form-control col" placeholder="Inserisci importo">
                  </div>
                </div>
                <div class="text-right">
                  <button class="btn btn-primary" type="submit">Salva</button>
                </div>
              </form>
            </BModal>
          </template>
          <CostiPersonaleListTable v-else-if="viewMode === 'list' && results" class="report-texts-size" :costi="results" :edit-enabled="userIsAdmin || userIsContextCompanyConsultant || userIsContextCompanyOwner" @delete="removeCostoPersonale" />
        </template>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '@/mixins/operativePage';
import loadingDataMixin from '@/mixins/loadingData';
import computedFinancialDataMixin from '@/mixins/computedFinancialData';
import confirmWithModalMixin from '@/libs/Confirm/mixins/confirmWithModal';
import formatMoneyFilter from '@/filters/formatMoney';
import { companyHasModule } from '@/helpers/modules';

import { BFormCheckbox, BModal } from 'bootstrap-vue';

export default {
  name: 'page-operative-company-data-analysis-costs-list',
  mixins: [operativePageMixin, loadingDataMixin, computedFinancialDataMixin, confirmWithModalMixin],
  components: {
    BModal,
    BFormCheckbox,
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle'),
    ErrorOnLoadingDataFeedback: () => import('@/views/components/Operative/ErrorOnLoadingDataFeedback'),
    LoadingDataLoader: () => import('@/views/components/Operative/LoadingDataLoader'),
    OperativeTable: () => import('@/views/components/Operative/OperativeTable'),
    OperativeTableBodyHeader: () => import('@/views/components/Operative/OperativeTableBodyHeader'),
    OperativeTableBodyRow: () => import('@/views/components/Operative/OperativeTableBodyRow'),
    OperativeTableEmptyBodyRow: () => import('@/views/components/Operative/OperativeTableEmptyBodyRow'),
    OperativeTableAggregateTable: () => import('@/views/components/Operative/OperativeTable/OperativeTableAggregateTable'),
    ForecastListSwitcher: () => import('@/views/components/Operative/ForecastListSwitcher'),
    CostiPersonaleListTable: () => import('@/views/components/Operative/ListTable/CostiPersonaleListTable'),
    AppRouterLink: () => import('@/views/components/Layout/AppRouterLink'),
  },
  filters: {
    money: formatMoneyFilter,
  },
  data () {
    return {
      data: null,
      results: null,
      viewMode: 'forecast',
      isSticky: true,
      currentModalInfo: {
        id: null,
        title: null,
        type: null,
        typeName: null,
        month: null,
        year: null,
        value: null,
      },
      openedRows: {},
    };
  },
  computed: {
    isModuleProjectsEnabled () {
      return companyHasModule(this.company, 'PROJECTS');
    },
    aggregateTableHeadings () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.convertToMonthsHeading(this.data[0].data);
    },
    aggregateTableRows () {
      if (!this.data || this.data.length < 0) {
        return [];
      }

      return this.data.map(d => ({
        id: d.id,
        name: d.name,
        data: this.convertToRowGroup(d.data, [
          'monthHoursAutomatico',
          'monthHoursBudget',
          'monthHoursConsuntivo',
          'monthHours',
          this.isModuleProjectsEnabled ? 'monthProjectsHours' : null,
          this.isModuleProjectsEnabled ? 'monthStructuralHours' : null,
          'ralAutomatico',
          'ralBudget',
          'ralConsuntivo',
          'ral',
          'altriCostiAutomatico',
          'altriCostiBudget',
          'altriCostiConsuntivo',
          'altriCosti',
          'oneriSocialiAutomatico',
          'oneriSocialiBudget',
          'oneriSocialiConsuntivo',
          'oneriSociali',
          'accantonamentoTfrAutomatico',
          'accantonamentoTfrBudget',
          'accantonamentoTfrConsuntivo',
          'accantonamentoTfr',
          'ritenuteAutomatico',
          'ritenuteBudget',
          'ritenuteConsuntivo',
          'ritenute',
          'fondoTfr',
          'debitoVDipendenti',
          'enabledRalBudgetEdit',
          'enabledRalConsuntivoEdit',
          'enabledAltriCostiBudgetEdit',
          'enabledAltriCostiConsuntivoEdit',
          'enabledOneriSocialiBudgetEdit',
          'enabledOneriSocialiConsuntivoEdit',
          'enabledAccTfrBudgetEdit',
          'enabledAccTfrConsuntivoEdit',
          'enabledMonthHoursBudgetEdit',
          'enabledMonthHoursConsuntivoEdit',
          'month',
          'year',
        ].filter(Boolean)),
      }));
    },
  },
  methods: {
    setViewMode (mode) {
      if (mode === 'forecast') {
        this.retrieveForecastData();
      } else if (mode === 'list') {
        this.retrieveListData();
      }
      this.viewMode = mode;
    },
    retrieveForecastData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getComputedCostiPersonale(this.company?.id)
        .then(res => {
          this.data = res?.data?.data || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    retrieveListData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();
      this.$api.getCostiPersonale(this.company?.id)
        .then(res => {
          this.results = res?.data?.costi || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    showAddBudgetModal (id, title, type, typeName, month, year, value) {
      this.currentModalInfo.id = id;
      this.currentModalInfo.title = title;
      this.currentModalInfo.type = type;
      this.currentModalInfo.typeName = typeName;
      this.currentModalInfo.month = month;
      this.currentModalInfo.year = year;
      this.currentModalInfo.value = value;
      this.$refs['edit-value-modal'].show();
    },
    saveCurrentModalInfo () {
      this.$api.saveCostoPersonaleValue(this.company.id, this.currentModalInfo.id, this.currentModalInfo.type, this.currentModalInfo.year, this.currentModalInfo.month, this.currentModalInfo.value)
        .then(() => {
          this.$refs['edit-value-modal'].hide();
          this.setViewMode('forecast');
        })
        .catch(this.$log.errors)
      ;
    },
    removeCostoPersonale (id) {
      this.confirm('Cancellare il costo del personale selezionato? L\'operazione è irreversibile', 'Ok', 'Annulla')
        .then(confirmed => {
          if (!confirmed) {
            return;
          }

          this.$api.deleteCostoPersonale(this.company.id, id).then(() => this.retrieveListData()).catch(this.$log.error);
        })
      ;
    },
    toggleRow (id) {
      if (undefined === this.openedRows[id]) {
        this.$set(this.openedRows, id, true);

        return;
      }

      this.$set(this.openedRows, id, !this.openedRows[id]);
    },
  },
  watch: {
    company: {
      immediate: true,
      handler: function () {
        this.setViewMode('forecast');
      },
    },
  },
};

</script>
